import type { Place } from "src/api/SearchResponse";
import type { Geocoded } from "src/GeocodedPlace";
import type { SearchPlace } from "src/utils/hooks/useSearch";
import type { HotelViewModel } from "src/domain/HotelsScreen/hotelsScreenAdapter";
import { useDistanceToPlace } from "src/utils/hooks/useDistanceToPlace";
import ChevronRight from "src/design-system/icons/ChevronRight.svg?react";
import type { PartialPlace } from "src/domain/HotelsScreen/HotelsScreen.types";
import { useNavigateToHotelsPage } from "src/utils/hooks/useNavigateToHotelsPage";
import { isPointKind } from "src/utils/geoKind";
import { useIntl } from "react-intl";
import { useScreenMinWidth } from "src/utils/hooks/useScreenMinWidth";
import { tw } from "src/design-system/tw";
import { Button as AriaButton } from "react-aria-components";
import StarSolid from "src/design-system/icons/StarSolid.svg?react";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { Skeleton } from "../../../../components/Skeleton/Skeleton";
import { PromoGallery } from "../SearchPromoGallery";
import { PromoPreviewContainer } from "../SearchPromoPreviewContainer";
import { PromoViewMoreButton } from "../SearchPromoViewMore";
import { PromoContainer } from "../SearchPromoContainer";
import messages from "./HotelPromo.messages";

export function HotelPromo({
  destination,
  hotelList,
}: {
  destination: SearchPlace | Place | Geocoded;
  hotelList: HotelViewModel[];
}) {
  const intl = useIntl();
  const { navigateToHotels } = useNavigateToHotelsPage();
  const { isMobile } = useScreenMinWidth();

  const filteredHotelsList = hotelList.slice(0, 3);
  const hotelDestination: PartialPlace = {
    lat: destination?.lat ?? 0,
    lng: destination?.lng ?? 0,
    kind: destination?.kind ?? "",
    shortName: destination?.shortName ?? "",
  };

  const copyPreposition = isPointKind(hotelDestination.kind) ? "Near" : "In";
  const headerMessage = messages[`hotels${copyPreposition}`];

  return (
    <PromoContainer navigateToHotels={navigateToHotels}>
      <h2 className="text-heading-md">
        {intl.formatMessage(headerMessage, {
          location: hotelDestination.shortName,
        })}
      </h2>
      <PromoGallery listLength={filteredHotelsList.length}>
        {filteredHotelsList.map((hotel) => (
          <HotelPreview
            key={hotel.hotelId}
            hotel={hotel}
            destination={hotelDestination}
            navigateToHotels={navigateToHotels}
          />
        ))}
      </PromoGallery>
      {!isMobile && (
        <ChevronButton
          navigateToHotels={navigateToHotels}
          ariaLabel={intl.formatMessage(messages.viewMore)}
        />
      )}

      <PromoViewMoreButton
        onPress={() => {
          sendAnalyticsInteractionEvent({
            category: "SearchResults",
            action: "Click:HotelPromoViewMore",
          });
          navigateToHotels();
        }}
      />
    </PromoContainer>
  );
}

function HotelPreview({
  hotel,
  destination,
  navigateToHotels,
}: {
  hotel: HotelViewModel;
  destination: PartialPlace;
  navigateToHotels: () => void;
}) {
  const formattedDistance = useDistanceToPlace(hotel.coordinates, destination);

  const hotelImage = (
    <img
      src={hotel.imageUrl}
      alt={hotel.title}
      className={tw(
        "h-32 w-full shrink-0 rounded-lg bg-no-repeat object-cover select-none",
        "group-hover:brightness-75 group-active:brightness-75",
      )}
      draggable="false"
      loading="lazy"
      width={150}
      height={150}
    />
  );

  return (
    <PromoPreviewContainer
      onPress={() => {
        sendAnalyticsInteractionEvent({
          category: "SearchResults",
          action: "Click:HotelPromoTopHotel",
        });
        navigateToHotels();
      }}
    >
      {hotelImage}
      <div className="flex flex-col gap-1">
        {hotel.review && (
          <RatingBadge
            rating={hotel.review?.score}
            numberOfReviews={hotel.review?.count}
          />
        )}
        <h3 className="line-clamp-1 text-label-lg font-semibold text-ellipsis">
          {hotel.title}
        </h3>
        <span className="text-label-md text-text-secondary">
          {formattedDistance}
        </span>
      </div>
    </PromoPreviewContainer>
  );
}

function RatingBadge({
  rating,
  numberOfReviews,
}: {
  rating: number;
  numberOfReviews: number;
}) {
  return (
    <div className="absolute top-2 left-2 flex items-center gap-1 rounded-md bg-surface px-2 py-1.5 drop-shadow">
      <StarSolid width={12} height={12} className="-mt-px" />
      <span className="text-label-sm font-semibold">{rating.toFixed(1)}</span>
      <span className="text-label-sm"> ({numberOfReviews})</span>
    </div>
  );
}

export function HotelPromoLoadingSkeleton() {
  return (
    <>
      <Skeleton height={24} borderRadius="rounded_lg" />
      <Skeleton height={216} borderRadius="rounded_lg" />
      <Skeleton height={44} width="100%" borderRadius="rounded_lg" />
    </>
  );
}

function ChevronButton({
  navigateToHotels,
  ariaLabel,
}: {
  navigateToHotels: () => void;
  ariaLabel: string;
}) {
  return (
    <AriaButton
      className="absolute top-1/2 right-2 flex cursor-pointer items-center rounded-full bg-surface p-2.5 drop-shadow hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
      onPress={() => {
        sendAnalyticsInteractionEvent({
          category: "SearchResults",
          action: "Click:HotelPromoChevronRight",
        });
        navigateToHotels();
      }}
      aria-label={ariaLabel}
    >
      <ChevronRight className="h-5 w-5 text-icon-primary" />
    </AriaButton>
  );
}
